/**
 * Contractors Module Types
 *
 * Covers contractor profiles, contracts, projects, time tracking,
 * invoicing, bonuses, reimbursements, availability, and schedules.
 */

// =============================================================================
// Status Unions
// =============================================================================

export type ContractStatus = 'draft' | 'active' | 'expired' | 'terminated' | 'renewed';
export type RateType = 'hourly' | 'fixed' | 'company_set';
export type PaymentTerms = 'net_15' | 'net_30' | 'net_60' | 'net_90' | 'on_completion';
export type BillingType = 'ett' | 'stt' | 'fixed' | 'company_set';
export type TimeEntryType = 'stt' | 'ett';
export type TimeEntryStatus = 'draft' | 'submitted' | 'approved' | 'rejected' | 'invoiced';
export type InvoiceStatus = 'draft' | 'pending' | 'paid' | 'overdue' | 'cancelled';
export type InvoiceLineSourceType = 'ett' | 'stt' | 'fixed' | 'company_set' | 'bonus' | 'reimbursement';
export type AvailabilityBlockType = 'available' | 'unavailable' | 'on_leave' | 'busy';
export type ScheduleStatus = 'draft' | 'pending_approval' | 'active' | 'rejected' | 'inactive';
export type ScheduleBlockType = 'work' | 'meeting' | 'admin' | 'other';
export type ProjectStatus = 'active' | 'on_hold' | 'completed' | 'cancelled';
export type ReimbursementStatus = 'submitted' | 'approved' | 'rejected' | 'invoiced';
export type RenewalType = 'auto' | 'manual' | 'none';

// =============================================================================
// Status Display Configs
// =============================================================================

export const CONTRACT_STATUS_CONFIG: Record<ContractStatus, { color: string; bgColor: string }> = {
  draft: { color: 'text-gray-700', bgColor: 'bg-gray-100' },
  active: { color: 'text-green-700', bgColor: 'bg-green-100' },
  expired: { color: 'text-orange-700', bgColor: 'bg-orange-100' },
  terminated: { color: 'text-red-700', bgColor: 'bg-red-100' },
  renewed: { color: 'text-blue-700', bgColor: 'bg-blue-100' },
};

export const INVOICE_STATUS_CONFIG: Record<InvoiceStatus, { color: string; bgColor: string }> = {
  draft: { color: 'text-gray-700', bgColor: 'bg-gray-100' },
  pending: { color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
  paid: { color: 'text-green-700', bgColor: 'bg-green-100' },
  overdue: { color: 'text-red-700', bgColor: 'bg-red-100' },
  cancelled: { color: 'text-gray-500', bgColor: 'bg-gray-50' },
};

export const TIME_ENTRY_STATUS_CONFIG: Record<TimeEntryStatus, { color: string; bgColor: string }> = {
  draft: { color: 'text-gray-700', bgColor: 'bg-gray-100' },
  submitted: { color: 'text-blue-700', bgColor: 'bg-blue-100' },
  approved: { color: 'text-green-700', bgColor: 'bg-green-100' },
  rejected: { color: 'text-red-700', bgColor: 'bg-red-100' },
  invoiced: { color: 'text-purple-700', bgColor: 'bg-purple-100' },
};

export const SCHEDULE_STATUS_CONFIG: Record<ScheduleStatus, { color: string; bgColor: string }> = {
  draft: { color: 'text-gray-700', bgColor: 'bg-gray-100' },
  pending_approval: { color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
  active: { color: 'text-green-700', bgColor: 'bg-green-100' },
  rejected: { color: 'text-red-700', bgColor: 'bg-red-100' },
  inactive: { color: 'text-gray-500', bgColor: 'bg-gray-50' },
};

export const PROJECT_STATUS_CONFIG: Record<ProjectStatus, { color: string; bgColor: string }> = {
  active: { color: 'text-green-700', bgColor: 'bg-green-100' },
  on_hold: { color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
  completed: { color: 'text-blue-700', bgColor: 'bg-blue-100' },
  cancelled: { color: 'text-gray-500', bgColor: 'bg-gray-50' },
};

export const REIMBURSEMENT_STATUS_CONFIG: Record<ReimbursementStatus, { color: string; bgColor: string }> = {
  submitted: { color: 'text-blue-700', bgColor: 'bg-blue-100' },
  approved: { color: 'text-green-700', bgColor: 'bg-green-100' },
  rejected: { color: 'text-red-700', bgColor: 'bg-red-100' },
  invoiced: { color: 'text-purple-700', bgColor: 'bg-purple-100' },
};

export const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// =============================================================================
// Core Interfaces
// =============================================================================

export interface ContractorProfile {
  id: string;
  user_id: number;
  tenant_id: string;
  display_name: string;
  company_name: string | null;
  vat_number: string | null;
  tax_id: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  postal_code: string | null;
  country: string | null;
  bank_name: string | null;
  iban: string | null;
  bic: string | null;
  skills: string[];
  vat_rate: number | null;
  vat_country: string | null;
  billing_types: BillingType[];
  hourly_rate: number | null;
  fixed_monthly_rate: number | null;
  company_set_gross: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  // Joined from users table
  username?: string;
  email?: string;
  full_name?: string;
}

export interface Contract {
  id: string;
  tenant_id: string;
  contractor_user_id: number;
  created_by_user_id: number;
  title: string;
  scope_of_work: string | null;
  status: ContractStatus;
  rate_type: RateType;
  rate_amount: number;
  currency: string;
  start_date: string;
  end_date: string | null;
  payment_terms: PaymentTerms | null;
  renewal_type: RenewalType | null;
  notice_period_days: number;
  pdf_s3_key: string | null;
  disk_file_id: string | null;
  signed_at: string | null;
  terminated_at: string | null;
  termination_reason: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  amendment_count?: number;
  contractor_name?: string;
}

export interface Project {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  client_name: string | null;
  status: ProjectStatus;
  start_date: string | null;
  end_date: string | null;
  color: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ContractAmendment {
  id: string;
  contract_id: string;
  amended_by_user_id: number;
  amendment_type: string;
  description: string;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  effective_date: string;
  pdf_s3_key: string | null;
  created_at: string;
}

export interface ContractorInvoice {
  id: string;
  tenant_id: string;
  contract_id: string;
  contractor_user_id: number;
  generated_by_user_id: number;
  invoice_number: string;
  description: string;
  amount_net: number;
  vat_rate: number;
  vat_amount: number;
  amount_gross: number;
  currency: string;
  status: InvoiceStatus;
  issued_date: string;
  due_date: string;
  paid_date: string | null;
  payment_reference: string | null;
  pdf_s3_key: string | null;
  disk_file_id: string | null;
  billing_month: string;
  period_start: string | null;
  period_end: string | null;
  created_at: string;
  updated_at: string;
  contractor_name?: string;
  line_items?: InvoiceLineItem[];
}

export interface InvoiceLineItem {
  id: string;
  invoice_id: string;
  source_type: InvoiceLineSourceType;
  source_id: string | null;
  project_id: string | null;
  description: string;
  quantity: number;
  unit_price: number;
  amount: number;
  sort_order: number;
  created_at: string;
  project_name?: string;
}

export interface TimeEntry {
  id: string;
  contractor_user_id: number;
  tenant_id: string;
  contract_id: string;
  project_id: string | null;
  entry_type: TimeEntryType;
  date: string;
  hours: number;
  description: string | null;
  external_reference: string | null;
  screenshot_url: string | null;
  status: TimeEntryStatus;
  submitted_at: string | null;
  reviewed_by_user_id: number | null;
  reviewed_at: string | null;
  review_notes: string | null;
  invoice_line_item_id: string | null;
  created_at: string;
  updated_at: string;
  project_name?: string;
  contract_title?: string;
}

export interface Bonus {
  id: string;
  contractor_user_id: number;
  tenant_id: string;
  contract_id: string;
  granted_by_user_id: number;
  amount: number;
  currency: string;
  reason: string;
  billing_month: string;
  invoice_line_item_id: string | null;
  created_at: string;
  contractor_name?: string;
}

export interface Reimbursement {
  id: string;
  contractor_user_id: number;
  tenant_id: string;
  contract_id: string;
  submitted_by_user_id: number;
  approved_by_user_id: number | null;
  amount: number;
  currency: string;
  description: string;
  receipt_file_id: string | null;
  status: ReimbursementStatus;
  billing_month: string | null;
  invoice_line_item_id: string | null;
  approved_at: string | null;
  created_at: string;
  contractor_name?: string;
}

export interface AvailabilityBlock {
  id: string;
  contractor_user_id: number;
  tenant_id: string;
  block_type: AvailabilityBlockType;
  start_date: string;
  end_date: string;
  start_time: string | null;
  end_time: string | null;
  notes: string | null;
  recurring: boolean;
  created_at: string;
}

export interface ContractorSchedule {
  id: string;
  contractor_user_id: number;
  tenant_id: string;
  name: string | null;
  status: ScheduleStatus;
  effective_from: string;
  effective_until: string | null;
  timezone: string;
  submitted_at: string | null;
  reviewed_by_user_id: number | null;
  reviewed_at: string | null;
  review_notes: string | null;
  created_at: string;
  updated_at: string;
  blocks?: ScheduleBlock[];
  contractor_name?: string;
}

export interface ScheduleBlock {
  id: string;
  schedule_id: string;
  project_id: string | null;
  day_of_week: number;
  start_time: string;
  end_time: string;
  block_type: ScheduleBlockType;
  notes: string | null;
  sort_order: number;
  created_at: string;
  project_name?: string;
  project_color?: string;
}

export interface TimeSummary {
  total_hours: number;
  total_ett_hours: number;
  total_stt_hours: number;
  fixed_amount: number;
  company_set_amount: number;
  total_bonuses: number;
  total_reimbursements: number;
  amount_net: number;
  vat_rate: number;
  vat_amount: number;
  amount_gross: number;
  line_count: number;
  by_project?: Array<{ project_id: string; project_name: string; hours: number }>;
}

// =============================================================================
// Request Types
// =============================================================================

export interface CreateContractorRequest {
  user_id: number;
  display_name: string;
  company_name?: string;
  vat_number?: string;
  tax_id?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  bank_name?: string;
  iban?: string;
  bic?: string;
  skills?: string[];
  vat_rate?: number;
  vat_country?: string;
  billing_types?: BillingType[];
  hourly_rate?: number;
  fixed_monthly_rate?: number;
  company_set_gross?: number;
  notes?: string;
}

export interface UpdateContractorRequest extends Partial<Omit<CreateContractorRequest, 'user_id'>> {}

export interface CreateContractRequest {
  contractor_user_id: number;
  title: string;
  scope_of_work?: string;
  rate_type: RateType;
  rate_amount: number;
  currency?: string;
  start_date: string;
  end_date?: string;
  payment_terms?: PaymentTerms;
  renewal_type?: RenewalType;
  notice_period_days?: number;
}

export interface UpdateContractRequest extends Partial<Omit<CreateContractRequest, 'contractor_user_id'>> {}

export interface CreateProjectRequest {
  name: string;
  description?: string;
  client_name?: string;
  start_date?: string;
  end_date?: string;
  color?: string;
}

export interface UpdateProjectRequest extends Partial<CreateProjectRequest> {
  status?: ProjectStatus;
}

export interface GenerateInvoiceRequest {
  contract_id: string;
  contractor_user_id: number;
  billing_month: string;
  description: string;
}

export interface InvoiceStatusUpdate {
  status: InvoiceStatus;
  paid_date?: string;
  payment_reference?: string;
}

export interface CreateTimeEntryRequest {
  contract_id: string;
  project_id?: string;
  entry_type: TimeEntryType;
  date: string;
  hours: number;
  description?: string;
  external_reference?: string;
  screenshot_url?: string;
}

export interface TimeEntryReviewRequest {
  entry_ids: string[];
  action: 'approve' | 'reject';
  notes?: string;
}

export interface CreateBonusRequest {
  contractor_user_id: number;
  contract_id: string;
  amount: number;
  currency?: string;
  reason: string;
  billing_month: string;
}

export interface CreateReimbursementRequest {
  contract_id: string;
  amount: number;
  currency?: string;
  description: string;
  receipt_file_id?: string;
}

export interface ReimbursementReviewRequest {
  action: 'approve' | 'reject';
  billing_month?: string;
  notes?: string;
}

export interface CreateAvailabilityRequest {
  block_type: AvailabilityBlockType;
  start_date: string;
  end_date: string;
  start_time?: string;
  end_time?: string;
  notes?: string;
}

export interface CreateScheduleRequest {
  name?: string;
  effective_from: string;
  effective_until?: string;
  timezone?: string;
}

export interface CreateScheduleBlockRequest {
  project_id?: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  block_type?: ScheduleBlockType;
  notes?: string;
}

export interface ScheduleReviewRequest {
  action: 'approve' | 'reject';
  notes?: string;
}
